<template>
  <div :id="rootElementId" :style="`height:${SVG_RENDER_HEIGHT}px`"></div>
</template>

<script>
import * as _ from "lodash";
import { extent } from "d3-array";
import { getDataUrl } from "../../utils/functions";
import { loadFonts, copyAndAppendElement } from "../../utils/exportToPNG";
import ResizeObserver from 'resize-observer-polyfill';

export default {
  name: "PassMapChart",
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false
    },
    margin: {
      type: Object,
      required: false
    },
    passes: {
      type: Array,
      required: false
    },
    passesByPlayersName: {
      type: Object,
      required: false
    },
    playersAvgPosition: {
      type: Array,
      required: false
    },
    teamId: {
      type: Number,
      required: false
    },
    teamFormation: {
      type: String,
      required: false
    },
    teamColor: {
      type: String,
      required: false
    },
    strokeColor: {
      type: String,
      required: false
    },
    teamName: {
      type: String,
      required: false
    },
    minFrom: {
      type: Number,
      required: false
    },
    minTo: {
      type: Number,
      required: false
    },
    language: {
      type: String,
      required: false
    },
    mediaMode: {
      type: Boolean,
      default: false
    },
    passesType: {
      type: String,
      default: 'all'
    },
    orientation: {
      type: String,
      default: 'horizontal' // 'vertical'
    }
  },
  computed: {
    SVG_WIDTH() {
      return this.size ? this.size : 380;
    },
    PITCH_WIDTH() {
      if (this.margin) {
        return this.SVG_WIDTH - this.margin.left - this.margin.right;
      } else {
        return this.SVG_WIDTH;
      }
    },
    PITCH_HEIGHT() {
      return this.PITCH_WIDTH * 0.67125;
    },
    isVertical() {
      return this.orientation === 'vertical';
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
    },
    SVG_RENDER_WIDTH() {
      return this.isVertical ? this.PITCH_HEIGHT : this.PITCH_WIDTH;
    },
    SVG_RENDER_HEIGHT() {
      return this.isVertical ? this.PITCH_WIDTH : this.PITCH_HEIGHT;
    },
    rootElementId() {
      return `pitchPassMap${this.id}`;
    },
    currentUserFieldColor() {
      return this.$store.getters["auth/fieldColor"];
    },
    typeOfPlayLabel() {
      const map = {
        all: this.$t('game.passMap.all'),
        isAttacking: this.$t('game.passMap.attacking'),
        isDefending: this.$t('game.passMap.defending'),
        isForward: this.$t('game.passMap.forwards'),
        isBackward: this.$t('game.passMap.backwards'),
        isSideways: this.$t('game.passMap.sideways'),
        isLongBall: this.$t('game.passMap.longBall')
      };
      return map[this.passesType] || map.all;
    }
  },
  data() {
    return {
      height: 0,
      width: 0
    };
  },
  async mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));

    const SVG_WIDTH = this.size ? this.size : 380;
    const isMobileDevice = SVG_WIDTH < 380 ? 1 : 0;
    const margin = this.margin
      ? this.margin
      : { top: 0, right: 0, bottom: 0, left: 0 };
    const PITCH_WIDTH = SVG_WIDTH - margin.left - margin.right;
    const PITCH_HEIGHT = PITCH_WIDTH * 0.67125;
    const CHART_SIZE_RATIO =
      SVG_WIDTH < 380 ? (SVG_WIDTH / 2) * 0.0026 : (SVG_WIDTH / 2) * 0.0022; //CHART_SIZE

    d3.selectAll(".tooltip").remove();

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip");

    const renderW = this.SVG_RENDER_WIDTH;
    const renderH = this.SVG_RENDER_HEIGHT;

    let svg = d3
      .select(`#${this.rootElementId}`)
      .append("svg")
      .attr("width", renderW)
      .attr("height", renderH)
      .attr("id",`${this.rootElementId}SVG`)
      .style("background-color", "#ffffff");

    // Pitch layer rotates 90deg CCW when vertical: (x, y) -> (y, PITCH_WIDTH - x)
    const pitchLayer = svg.append("g").attr("class", "pitch-layer");
    if (this.isVertical) {
      pitchLayer.attr("transform", `translate(${margin.left},${margin.top + PITCH_WIDTH}) rotate(-90)`);
    } else {
      pitchLayer.attr("transform", `translate(${margin.left},${margin.top})`);
    };
    // UI layer never rotates (header, legend, arrow, logo).
    const uiLayer = svg.append("g").attr("class", "ui-layer");

    //Render Green Pitch
    this.drawImageBase64(
      pitchLayer,
      0,
      0,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      require(`@/assets/img/pitch_${this.currentUserFieldColor}.png`),
      true
    );

    let passesByPlayer = _.chain(this.passes)
      .groupBy("playerFromId")
      .map((passesGrouped, id) => {
        let pass = _.first(passesGrouped);
        let x = _.get(
          this.playersAvgPosition.filter(p => p.playerId == id),
          "[0].x"
        );
        let y = _.get(
          this.playersAvgPosition.filter(p => p.playerId == id),
          "[0].y"
        );

        let countPasses = _.chain(this.passes)
          .filter(p => p.playerFromId == id)
          .size()
          .value();
        let name = pass.playerFromName;

        let r = countPasses / 50;
        let opacity = 0.5;
        return { x, y, countPasses, name, r, opacity };
      })
      .value();

    //Unify passes (Just count as 1 the pass between player A and player B)
    let passesUnified = this.unifyPasses(this.passesByPlayersName);

    let segments = this.computePassLines(passesByPlayer, passesUnified);

    this.printPassMapTextsLegendAndLogo(uiLayer, renderW, renderH, margin, CHART_SIZE_RATIO);
    this.renderSegments(
      pitchLayer,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      segments,
      this.teamColor,
      isMobileDevice,
      CHART_SIZE_RATIO
    );
    this.renderCircles(
      pitchLayer,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      passesByPlayer,
      this.teamColor,
      this.strokeColor,
      CHART_SIZE_RATIO,
      tooltip,
      isMobileDevice
    );
    let font = await loadFonts();
    copyAndAppendElement(svg.node(), font.node(), 0, 0);

    //Emit when is loaded
    this.$emit("loaded", this.rootElementId);
    this.$emit("sizeChange", { width: PITCH_WIDTH, height: PITCH_HEIGHT });
  },
  methods: {
    /** Legend PNGs exist as english / spanish only; use spanish assets only for es (matches EventsChart). */
    graphicLangSuffix() {
      return this.language === 'es' ? 'spanish' : 'english';
    },
    renderCircles(
      svg,
      chartWidth,
      chartHeight,
      players,
      teamColor,
      strokeColor,
      chartSizeRatio,
      tooltip,
      isMobileDevice
    ) {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      // Remove DUPS
      const circles = [
        ...new Map(players.map(obj => [JSON.stringify(obj), obj])).values()
      ];

      let x = d3.scale
        .linear()
        .domain([0 - 100 * 0.093, 100 + 100 * 0.098])
        .range([chartWidth, 0]);
      let y = d3.scale
        .linear()
        .domain([0 - 100 * 0.098, 100 + 100 * 0.098])
        .range([chartHeight, 0]);
      //let radius = d3.scale.linear().domain(radiusDomain).range([4, 75]);
      let radius = d3.scale
        .linear()
        .domain([0, 1])
        .range([chartWidth * 0.0055, chartHeight * 0.024]);

      for (let circle of circles) {
        if (!circle.x || !circle.y) continue;
        let sx = x(100 - circle.x) + isMobileDevice * 9.5;
        let sy = y(circle.y);
        //Draw circles
        this.drawCircle(
          svg,
          sx,
          sy,
          circle,
          radius(circle.r) / chartSizeRatio,
          teamColor,
          "#000000",
          chartWidth,
          chartSizeRatio,
          tooltip,
          isMobileDevice
        );
        const yCoord = sy - (this.isVertical ? 30 : 15) - radius(circle.r) + isMobileDevice * 8;
        const xCoord = sx - this.measureText(circle.name, (this.isVertical ? 2.7 : 14) * chartSizeRatio) / 2;

        //Write players name over circles
        const nameText = this.writeText(
          svg,
          xCoord,
          yCoord,
          "center",
          (this.isVertical ? 13 : 17) * chartSizeRatio + "px",
          circle.name,
          textColor
        );
        // Keep names upright when the pitch layer is rotated to vertical.
        if (this.isVertical) {
          nameText.attr("transform", `rotate(90, ${xCoord}, ${yCoord})`);
        }
      }
    },
    renderSegments(
      svg,
      chartWidth,
      chartHeight,
      segments,
      teamColor,
      isMobileDevice,
      chartSizeRatio,
      widthDomain = null
    ) {
      widthDomain =
        widthDomain !== null ? [0, 60] : extent(segments, c => c.width);
      let x = d3.scale
        .linear()
        .domain([0 - 100 * 0.093, 100 + 100 * 0.098])
        .range([chartWidth, 0]);
      let y = d3.scale
        .linear()
        .domain([0 - 100 * 0.098, 100 + 100 * 0.098])
        .range([chartHeight, 0]);
      let width = d3.scale
        .linear()
        .domain(widthDomain)
        .range([1, 10]);
      // ====================================================================
      //        Render Segments
      // ====================================================================

      for (let segment of segments.filter(
        p => p.x0 !== null && p.y0 !== null && p.x1 !== null && p.y1 !== null
      )) {
        let sx0 = x(100 - segment.x0) + isMobileDevice * 9.5;
        let sy0 = y(segment.y0);
        let sx1 = x(100 - segment.x1) + isMobileDevice * 9.5;
        let sy1 = y(segment.y1);
        this.drawLine(
          svg,
          sx0,
          sy0,
          sx1,
          sy1,
          width(segment.width) * chartSizeRatio,
          teamColor,
          0.4
        );
      }
    },
    drawLine(svg, x0, y0, x1, y1, width, strokeColor, opacity = 1) {
      svg
        .append("line")
        .attr("x1", x0)
        .attr("y1", y0)
        .attr("x2", x1)
        .attr("y2", y1)
        .attr("stroke-width", width)
        .attr("stroke-linecap", "round")
        .style("opacity", opacity)
        .attr(
          "stroke",
          strokeColor ? strokeColor.replace("0x", "#") : "#0000FF"
        );
    },
    drawCircle(
      svg,
      x,
      y,
      circle,
      radius,
      teamColor,
      strokeColor,
      chartWidth,
      chartSizeRatio,
      tooltip,
      isMobileDevice
    ) {
      let self = this;
      svg
        .append("circle")
        .attr("r", radius * chartSizeRatio)
        .attr("cx", x)
        .attr("cy", y)
        .attr("id", "pass-" + circle.name + `-${this.rootElementId}`)
        .style("fill-opacity", 0.8)
        .style("opacity", 0.8)
        .style("stroke", strokeColor)
        .style("stroke-width", 1.5 * chartSizeRatio)
        .style("fill", teamColor ? teamColor.replace("0x", "#") : "#0000FF")
        .on("mouseenter", function(d) {
          d3.select(this)
            .transition()
            .duration(300)
            .attr("stroke-width", 3)
            .attr("r", d => radius * 1.3 * chartSizeRatio);

          let passesId = document.getElementById(
            "pass-" + circle.name + `-${self.rootElementId}`
          );
          const passesLabel = self.$t("game.passMap.successfullPasses");
          const tooltipSuffix = " " + passesLabel;
          let maxTextWidth = self.measureText(
            circle.name + " • " + circle.countPasses + tooltipSuffix,
            16 * chartSizeRatio
          );
          let widthTooltip =
            chartSizeRatio > 0.5 ? maxTextWidth + 75 : maxTextWidth + 40;
          let height =
            chartSizeRatio > 0.5 ? 50 * chartSizeRatio : 70 * chartSizeRatio;
          let heightTooltip =
            chartWidth * 0.05 + isMobileDevice * 2.5 + 16 * chartSizeRatio;

          let topOffset = 110 * chartSizeRatio;
          let leftOffset =
            chartSizeRatio > 0.5
              ? Math.ceil(passesId.getAttribute("r"))
              : Math.ceil(passesId.getAttribute("r")) - 0.5;

          d3.select("body > .tooltip")
            .style("width", widthTooltip + "px")
            .style("height", heightTooltip + "px")
            .style("font-size", 16 * chartSizeRatio + "px")
            .style("padding-top", 10 * chartSizeRatio + "px")
            .style(
              "left",
              passesId.getBoundingClientRect().left -
                widthTooltip / 2 +
                leftOffset +
                "px"
            )
            .style("top", event.pageY - topOffset + "px")
            .style("display", "inline-block")
            .html(
              "<b>" +
                circle.name +
                "</b></br> " +
                circle.countPasses +
                " " +
                passesLabel
            );
        })
        .on("mouseout", function() {
          d3.select("body > .tooltip").style("display", "none");
          d3.select(this)
            .transition()
            .duration(300)
            .attr("r", d => radius * chartSizeRatio);
        });
    },
    computePassLines(players, passes) {
      let passLines = [];
      let playersByName = players.reduce((acc, p) => {
        acc[p.name] = p;
        return acc;
      }, {});
      for (let player of players) {
        const fromP = player.name;
        // After unifyPasses(), some passers may only appear nested (bidirectional merge) or
        // filters may leave no outgoing edges for this name — skip without drawing lines.
        if (!(fromP in passes)) continue;
        for (let toP in passes[fromP]) {
          let count = passes[fromP][toP];
          if (!(toP in playersByName)) continue;

          if (playersByName[toP].x !== null && playersByName[toP].y)
            passLines.push({
              x0: player.x,
              y0: player.y,
              x1: playersByName[toP].x,
              y1: playersByName[toP].y,
              width: count
            });
        }
      }

      return passLines;
    },
    unifyPasses(passes) {
      let unifiedPasses = {};
      for (let fromP in passes) {
        for (let toP in passes[fromP]) {
          if (
            toP in unifiedPasses &&
            unifiedPasses[toP] &&
            fromP in unifiedPasses[toP]
          ) {
            unifiedPasses[toP][fromP] += passes[toP][fromP];
          } else {
            if (!(fromP in unifiedPasses)) {
              unifiedPasses[fromP] = {};
            }
            if (!(toP in unifiedPasses[fromP])) {
              unifiedPasses[fromP][toP] = passes[fromP][toP];
            } else {
              unifiedPasses[fromP][toP] += passes[fromP][toP];
            }
          }
        }
      }
      return unifiedPasses;
    },
    printPassMapTextsLegendAndLogo(svg, pitchSize, pitchHeight, margin) {
      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';

      const isMobilePortrait = this.isMobile && this.isVertical;

      const typeOfPlayStr = this.$t('game.passMap.typeOfPlay') + " " + this.typeOfPlayLabel;
      const passesStr = this.$t('game.passMap.passes') + " " + this.passes.length;
      const minutesStr = this.$t('game.passMap.minutes') + " " + this.minFrom + "' - " + this.minTo + "'";
      const teamStr = this.teamName + " • " + this.teamFormation;

    if (isMobilePortrait) {
        // Two Rows Layout
        const headerH = pitchHeight * 0.11;
        const rectW = pitchSize;
        const rectX = margin.left;
        const rectY = margin.top;
        const rectOpacity = this.currentUserFieldColor === 'white' ? 0.15 : 0.7;
        const divisorColor = this.currentUserFieldColor === 'white' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)';

        // Rect Background
        svg.append("rect")
          .attr("x", rectX)
          .attr("y", rectY)
          .attr("width", rectW)
          .attr("height", headerH)
          .attr("class", "passMapHeaderRect")
          .style("fill", "black")
          .style("opacity", rectOpacity);

        const fontSize = Math.max(pitchSize * 0.022, 9.5);
        const fontSizeStr = fontSize + "px";

        // Row 1: TeamName
        const row1Y = rectY + headerH * 0.28;
        const crestSize = headerH * 0.35;
        const teamTextW = this.measureText(teamStr, fontSize);
        const totalTeamW = crestSize + 6 + teamTextW;
        const startX = rectX + (rectW - totalTeamW) / 2;

        this.drawImageBase64(
          svg,
          startX,
          row1Y - crestSize / 2,
          crestSize,
          crestSize,
          "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/" + this.teamId + ".png"
        );

        this.writeText(
          svg,
          startX + crestSize + 6,
          row1Y,
          "start",
          fontSizeStr,
          teamStr,
          textColor,
          "none",
          "0.35em"
        );

        // Horizontal Divider
        svg.append("line")
          .attr("x1", rectX + rectW * 0.02)
          .attr("y1", rectY + headerH * 0.53)
          .attr("x2", rectX + rectW * 0.98)
          .attr("y2", rectY + headerH * 0.53)
          .attr("stroke", divisorColor)
          .attr("stroke-width", 1);

        // Row 2: Type of Play, Passes, Minutes
        const row2Y = rectY + headerH * 0.78;
        const colW = rectW / 3;
        const row2FontSize = Math.min(fontSize * 0.85, (colW - 8) / 11);

        [typeOfPlayStr, passesStr, minutesStr].forEach((text, idx) => {
          const colCenter = rectX + colW * idx + colW / 2;
          this.writeText(
            svg,
            colCenter,
            row2Y,
            "middle",
            row2FontSize + "px",
            text,
            textColor,
            "none",
            "0.35em"
          );

          if (idx < 2) {
            const divX = rectX + colW * (idx + 1);
            svg.append("line")
              .attr("x1", divX)
              .attr("y1", rectY + headerH * 0.58)
              .attr("x2", divX)
              .attr("y2", rectY + headerH * 0.92)
              .attr("stroke", divisorColor)
              .attr("stroke-width", 1);
          }
        });

      }
      else {
        const headerH = pitchHeight * (this.isVertical ? 0.078 : 0.085);
        const rectW = this.isMobile && this.isVertical ? pitchSize * 1.1 : pitchSize * 0.84;
        const rectX = margin.left + pitchSize * (this.isMobile && this.isVertical ? 0.001 : 0.08);
        const rectY = margin.top + pitchHeight * 0;
        const rectOpacity = this.currentUserFieldColor === 'white' ? 0.15 : 0.7;
        const textY = rectY + headerH / 2;
        const divisorColor = this.currentUserFieldColor === 'white' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)';
        const pad = rectW * 0.015;
        const crestGap = rectW * (this.isVertical ? 0.05 : 0.035);

        //Measure content and compute dynamic layout
        const baseFontSize = Math.min(0.016 * pitchSize, rectW * 0.022);

        let crestSize = Math.min(headerH * 0.5, rectW * 0.12);
        let teamTextW = this.measureText(teamStr, baseFontSize);
        let s1Min = crestSize + crestGap + teamTextW + pad * 2;
        let s2Min = this.measureText(typeOfPlayStr, baseFontSize) + pad * 2;
        let s3Min = this.measureText(passesStr, baseFontSize) + pad * 2;
        let s4Min = this.measureText(minutesStr, baseFontSize) + pad * 2;
        let totalMin = s1Min + s2Min + s3Min + s4Min;

        const scale = totalMin > rectW ? Math.max(rectW / totalMin, 0.5) : 1;
        const fontSize = Math.max(baseFontSize * scale, 9);
        const fontSizeStr = fontSize + "px";
        crestSize = Math.max(crestSize * scale, headerH * 0.3);

        teamTextW = this.measureText(teamStr, fontSize);
        s2Min = this.measureText(typeOfPlayStr, fontSize) + pad * 2;
        s3Min = this.measureText(passesStr, fontSize) + pad * 2;
        s4Min = this.measureText(minutesStr, fontSize) + pad * 2;
        s1Min = crestSize + crestGap + teamTextW + pad * 2;
        totalMin = s1Min + s2Min + s3Min + s4Min;

        const s1W = Math.min(Math.max(s1Min, rectW * 0.22), rectW * 0.5);
        const remaining = Math.max(0, rectW - s1W);
        const s234Total = s2Min + s3Min + s4Min;
        const s2W = s234Total > 0 ? remaining * (s2Min / s234Total) : remaining / 3;
        const s3W = s234Total > 0 ? remaining * (s3Min / s234Total) : remaining / 3;
        const s4W = s234Total > 0 ? remaining * (s4Min / s234Total) : remaining / 3;

        const d1 = rectX + s1W;
        const d2 = rectX + s1W + s2W;
        const d3 = rectX + s1W + s2W + s3W;
        const s1Center = rectX + s1W / 2;
        const s2Center = rectX + s1W + s2W / 2;
        const s3Center = rectX + s1W + s2W + s3W / 2;
        const s4Center = rectX + s1W + s2W + s3W + s4W / 2;

        //Black rectangle behind header
        svg.append("rect")
          .attr("x", rectX)
          .attr("y", rectY)
          .attr("width", rectW)
          .attr("height", headerH)
          .attr("class", "passMapHeaderRect")
          .style("fill", "black")
          .style("opacity", rectOpacity);

        //Render team crest (section 1)
        const crestX = rectX + pad;
        const orientationCrestSize = this.isVertical ? crestSize * 0.75 : crestSize;
        const crestUrl = `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${this.teamId}.png`;
        this.drawImageBase64(svg, crestX, rectY + (headerH - orientationCrestSize) / 2, orientationCrestSize, orientationCrestSize, crestUrl);

        //Team name and formation (section 1) - centered in section
        this.writeText(
          svg,
          s1Center,
          textY,
          "middle",
          fontSizeStr,
          teamStr,
          textColor,
          "none",
          "0.35em"
        );

        //Divisor 1
        svg.append("line")
          .attr("x1", d1)
          .attr("y1", rectY + headerH * 0.15)
          .attr("x2", d1)
          .attr("y2", rectY + headerH * 0.85)
          .attr("stroke", divisorColor)
          .attr("stroke-width", 1);

        //Type of play (section 2) - centered
        this.writeText(
          svg,
          s2Center,
          textY,
          "middle",
          fontSizeStr,
          typeOfPlayStr,
          textColor,
          "none",
          "0.35em"
        );

        //Divisor 2
        svg.append("line")
          .attr("x1", d2)
          .attr("y1", rectY + headerH * 0.15)
          .attr("x2", d2)
          .attr("y2", rectY + headerH * 0.85)
          .attr("stroke", divisorColor)
          .attr("stroke-width", 1);

        //Passes (section 3) - centered
        this.writeText(
          svg,
          s3Center,
          textY,
          "middle",
          fontSizeStr,
          passesStr,
          textColor,
          "none",
          "0.35em"
        );

        //Divisor 3
        svg.append("line")
          .attr("x1", d3)
          .attr("y1", rectY + headerH * 0.15)
          .attr("x2", d3)
          .attr("y2", rectY + headerH * 0.85)
          .attr("stroke", divisorColor)
          .attr("stroke-width", 1);

        //Minutes (section 4) - centered
        this.writeText(
          svg,
          s4Center,
          textY,
          "middle",
          fontSizeStr,
          minutesStr,
          textColor,
          "none",
          "0.35em"
        );
      }

      this.drawAttackArrow(svg, PITCH_WIDTH, PITCH_HEIGHT);

      //Legend text (Touches)
      this.writeText(
        svg,
        pitchSize * (this.isVertical ? 0.21 : 0.41),
        pitchHeight * (this.isVertical ? 0.971 : 0.96),
        "middle",
        0.013 * pitchSize + "px",
        this.$t('game.gamePossessionMap.touches'),
        textColor,
        "none",
        "0.35em"
      );

      //Legend text (Passes)
      this.writeText(
        svg,
        pitchSize * (this.isVertical ? 0.33 : 0.535),
        pitchHeight * (this.isVertical ? 0.971 : 0.96),
        "middle",
        0.013 * pitchSize + "px",
        this.$t('game.gamePossessionMap.passes'),
        textColor,
        "none",
        "0.35em"
      );

      //Touches and passes legend in center
      let legendImage = require(`@/assets/img/legend${this.currentUserFieldColor === 'white' ? "_dark": ""}.png`);
      const legendW = pitchSize * 0.38;
      const legendX = this.SVG_RENDER_WIDTH * (this.isVertical ? 0.3 : 0.5) - legendW / 2;
      const legendY = pitchHeight * (this.isVertical ? 0.918 : 0.91) + margin.top;
      this.drawImageBase64(
        svg,
        legendX,
        legendY,
        legendW,
        pitchHeight * 0.1,
        legendImage
      );

      //Legend text (Player Location)
      this.writeText(
        svg,
        pitchSize * (this.isVertical ? 0.39 : 0.57),
        pitchHeight * (this.isVertical ? 0.962 : 0.94),
        "start",
        0.013 * pitchSize + "px",
        `${this.$t('game.passMap.playerLocation')}:`,
        textColor,
        "none",
        "0.35em"
      );

      //Legend text (Average Touch Position)
      this.writeText(
        svg,
        pitchSize * (this.isVertical ? 0.39 : 0.57),
        pitchHeight * (this.isVertical ? 0.976 : 0.97),
        "start",
        0.013 * pitchSize + "px",
        this.$t('game.passMap.passLegend'),
        textColor,
        "none",
        "0.35em"
      );

      //Driblab logo on the right
      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
      this.drawImageBase64(
        svg,
        pitchSize * 0.77 + margin.left,
        pitchHeight * (this.isVertical ? 0.926 : 0.92) + margin.top,
        pitchSize * 0.14,
        pitchHeight * 0.07,
        logo
      );
    },
    writeText(
      svg,
      x,
      y,
      anchor,
      fontSize,
      text,
      fill = "white",
      className = "none",
      dy = "0.8em",
      fontWeight = 500
    ) {
      return svg
        .append("text")
        .attr("x", x)
        .attr("y", y)
        .attr("text-anchor", anchor)
        .attr("dy", dy)
        .attr("font-weight", fontWeight)
        .attr("class", className)
        .style("font-size", fontSize)
        .style("font-family", "NeueMontreal")
        .style("fill", fill)
        .text(text);
    },
    async drawAttackArrow(svg, pitchWidth, pitchHeight) {
      const textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      const darkCode = this.currentUserFieldColor === 'white' ? '_dark' : '';
      const arrowImg = require(`@/assets/img/attack_direction${darkCode}.png`);
      //Draw arrow first
      const arrowWidth = pitchWidth * 0.12;
      const arrowHeight = arrowWidth * 0.2;
      const arrowX = pitchWidth * (this.isVertical ? -0.04 : 0.04);
      const arrowY = pitchHeight * 0.94;
      const rotation = this.isVertical ? -90 : 0;
      await this.drawImageBase64(svg, arrowX * (this.isVertical ? 0.75 : 1), arrowY * (this.isVertical ? 1.31 : 1), arrowWidth, arrowHeight, arrowImg, false, rotation);

      const textFontSize = this.isVertical ? 0.02 * pitchWidth + 'px' : 0.018 * pitchWidth + 'px';
      const textX = this.isVertical ? arrowX + arrowWidth / 2 : arrowX + pitchWidth * 0.09;
      const textY = this.isVertical ? arrowY + arrowHeight + pitchHeight * 0.098 : pitchHeight * 0.945;

      //Attacking direction text
      this.writeText(svg, textX, textY, this.isVertical ? 'middle' : 'start', textFontSize, this.$t('general.attackingDirection'), textColor, 'none', '0.8em', 800 );
      if (this.isVertical) {
        const text = svg.select('text:last-child');
        text.attr('transform', `rotate(-90, ${textX}, ${textY})`);
      };
    },
    drawImageBase64(svg, x, y, width, height, img, bottom = false, rotation = 0) {
      let base_image = new Image();
      base_image.crossOrigin = "anonymous";
      base_image.src = img;
      base_image.onload = event => {
        const dataUrl = getDataUrl(event.currentTarget);
        this.drawImage(svg, x, y, width, height, dataUrl, bottom, rotation);
      };
    },
    drawImage: function(svg, x, y, width, height, img, bottom = false, rotation = 0) {
      let imgNode = bottom
        ? svg.insert("svg:image", ":first-child")
        : svg.append("svg:image");
      const cx = x + width / 2;
      const cy = y + height / 2;
      imgNode
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img)
        .attr("transform", rotation ? `rotate(${rotation}, ${cx}, ${cy})` : null);
    },
    measureText(string, fontSize = 10) {
      const widths = [
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0.278125,
        0.278125,
        0.35625,
        0.55625,
        0.55625,
        0.890625,
        0.6671875,
        0.1921875,
        0.334375,
        0.334375,
        0.390625,
        0.584375,
        0.278125,
        0.334375,
        0.278125,
        0.303125,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.278125,
        0.278125,
        0.5859375,
        0.584375,
        0.5859375,
        0.55625,
        1.015625,
        0.6671875,
        0.6671875,
        0.7234375,
        0.7234375,
        0.6671875,
        0.6109375,
        0.778125,
        0.7234375,
        0.278125,
        0.5,
        0.6671875,
        0.55625,
        0.834375,
        0.7234375,
        0.778125,
        0.6671875,
        0.778125,
        0.7234375,
        0.6671875,
        0.6109375,
        0.7234375,
        0.6671875,
        0.9453125,
        0.6671875,
        0.6671875,
        0.6109375,
        0.278125,
        0.35625,
        0.278125,
        0.478125,
        0.55625,
        0.334375,
        0.55625,
        0.55625,
        0.5,
        0.55625,
        0.55625,
        0.278125,
        0.55625,
        0.55625,
        0.2234375,
        0.2421875,
        0.5,
        0.2234375,
        0.834375,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.334375,
        0.5,
        0.278125,
        0.55625,
        0.5,
        0.7234375,
        0.5,
        0.5,
        0.5,
        0.35625,
        0.2609375,
        0.3546875,
        0.590625
      ];
      const avg = 0.5293256578947368;

      return (
        string
          .split("")
          .map(c =>
            c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg
          )
          .reduce((cur, acc) => acc + cur) * fontSize
      );
    },
    onResize(){
      let elto =document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    }
  },
  destroyed() {
    delete this.observer;
  },
};
</script>


// WEBPACK FOOTER //
// src/views/base/PassMapChart.vue